import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { EASE } from '../brand';

/**
 * White app-surface card the mockups sit in. Mirrors the real app chrome
 * (light surface, soft border, generous radius) so the previews read as product.
 */
export function MockCard({ children, className = '' }) {
  return (
    <div
      className={`overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-[0_24px_60px_-20px_rgba(8,20,45,0.35)] ${className}`}
    >
      {children}
    </div>
  );
}

export function MockHeader({ title, subtitle, live = false, right = null }) {
  return (
    <div className="flex items-center justify-between gap-3 border-b border-slate-100 bg-slate-50/70 px-4 py-3">
      <div className="min-w-0">
        <p className="truncate text-sm font-semibold text-slate-900">{title}</p>
        {subtitle && <p className="truncate text-xs text-slate-500">{subtitle}</p>}
      </div>
      {live ? <LiveDot /> : right}
    </div>
  );
}

export function LiveDot({ label = 'Live' }) {
  const reduce = useReducedMotion();
  return (
    <span className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-emerald-700 ring-1 ring-inset ring-emerald-200">
      <motion.span
        className="h-1.5 w-1.5 rounded-full bg-emerald-500"
        animate={reduce ? undefined : { opacity: [1, 0.25, 1], scale: [1, 0.8, 1] }}
        transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
      />
      {label}
    </span>
  );
}

/** Row that slides in from the left, staggered by index, the first time it's seen. */
export function StaggerRow({ children, index = 0, className = '' }) {
  const reduce = useReducedMotion();
  if (reduce) return <div className={className}>{children}</div>;
  return (
    <motion.div
      className={className}
      initial={{ opacity: 0, x: -12 }}
      whileInView={{ opacity: 1, x: 0 }}
      viewport={{ once: true, margin: '-40px' }}
      transition={{ duration: 0.45, delay: 0.15 + index * 0.06, ease: EASE }}
    >
      {children}
    </motion.div>
  );
}

/** Horizontal bar that grows to `pct` when scrolled into view. */
export function MeterBar({ pct, className = 'bg-blue-600', delay = 0 }) {
  const reduce = useReducedMotion();
  return (
    <div className="h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
      <motion.div
        className={`h-full rounded-full ${className}`}
        initial={reduce ? false : { width: 0 }}
        whileInView={{ width: `${pct}%` }}
        viewport={{ once: true, margin: '-40px' }}
        transition={{ duration: 1, delay, ease: EASE }}
      />
    </div>
  );
}
